import DOMPurify from "dompurify";
import { Event } from "../types/event";
import { Room } from "../types/room";
import { UserState } from "../types/redux";
import _ from "lodash";
import {
  getHourFromMilliseconds,
  getMinutesFromMilliseconds,
  getAmOrPm,
} from "./utils";

const getNewValue = (oldValue: any, newValue: any) => {
  if (_.isArray(oldValue)) {
    return !_.isEqual(oldValue, newValue) ? newValue : undefined;
  } else {
    return oldValue !== newValue ? newValue : undefined;
  }
};

export const prepareEditEventData = (
  data: any,
  startDateTime: any,
  endDateTime: any,
  newEventHero: any,
  event: Event,
  eventType: string,
  placeholderFileName: string,
  userInfo: UserState,
  status?: string
) => {
  const editEventData: any = {
    title: getNewValue(event.title, data.title),
    description: getNewValue(
      event.description,
      DOMPurify.sanitize(data.description)
    ),
    startDateTime: getNewValue(event.startDateTime, startDateTime),
    endDateTime: getNewValue(event.endDateTime, endDateTime),
    dressCode: getNewValue(event.dressCode, data.dressCode),
    admissionCost: getNewValue(event.admissionCost, data.admissionCost),
    format: getNewValue(event.format, data.format),
    eventTags: getNewValue(event.eventTags, data.eventTags),
    transportationParkingDetails: getNewValue(
      event.transportationParkingDetails,
      data.transportationParkingDetails
    ),
    feedbackLink: getNewValue(event.feedbackLink, data.feedbackLink),
    primaryContacts: getNewValue(
      event.primaryContacts,
      data.primaryContacts
    ).map((contact: { name: String; email: string }) => contact.name),
    includeTeamsLink: getNewValue(
      event.teamsLink !== "" ? "yes" : "no",
      data.includeTeamsLink
    ),
    teamsLink: getNewValue(event.teamsLink, data.teamsLink),
    signUpSurvey: getNewValue(event.signUpSurvey, data.signUpSurvey),
    rooms: getNewValue(event.rooms, data.rooms),
    eventType: getNewValue(event.eventType, data.eventType),
    location: (() => {
      if (data.format === "Virtual") {
        return getNewValue(event.location, "Virtual");
      } else if (data.eventType === "Off-site") {
        return getNewValue(event.location, data.location);
      } else if (data.eventType === "On-site") {
        if (data.rooms.length === 0) {
          const officeLocationMapping: { [key: string]: string } = {
            Boston: "399 Boylston St #1000, Boston, MA 02116",
            Hartford: "185 Asylum St #3201, Hartford, CT 06103",
            "Boston & Hartford (New England)":
              "399 Boylston St #1000, Boston, MA 02116 (Boston Office) & 185 Asylum St #3201, Hartford, CT 06103 (Hartford Office)",
          };
          return getNewValue(
            event.location,
            officeLocationMapping[data.office] || data.office
          );
        } else {
          return getNewValue(
            event.location,
            data.rooms.map((room: Room) => room.name).join(", ")
          );
        }
      }
      return getNewValue(event.location, "");
    })(),
    office: getNewValue(event.office, data.office),
    recipientList: (() => {
      const newValue = getNewValue(event.recipientList, data.recipientList);
      return Array.isArray(newValue) &&
        newValue.length === 1 &&
        newValue[0] === null
        ? null
        : newValue;
    })(),
    googleMapsLocationLink: getNewValue(
      event.googleMapsLocationLink,
      data.googleMapsLocationLink
    ),
    status: status,
  };

  if (data.eventImageFile && placeholderFileName === "FILE_CHANGED") {
    editEventData.eventHero = newEventHero;
    editEventData.eventOrganizerName = userInfo.name;
    editEventData.eventOrganizerTitle = userInfo.jobTitle;
    editEventData.eventOrganizerEmail = userInfo.email;
  } else if (placeholderFileName === "FILE_CHANGED" && !data.eventImageFile) {
    editEventData.eventHero = "";
  }

  return editEventData;
};

export const formatEventTime = (startDateTime: number, endDateTime: number) => {
  const startTime = {
    time: `${getHourFromMilliseconds(startDateTime)}${
      getMinutesFromMilliseconds(startDateTime) !== "00"
        ? `:${getMinutesFromMilliseconds(startDateTime)}`
        : ""
    }`,
    ampm: getAmOrPm(startDateTime),
  };

  const endTime = {
    time: `${getHourFromMilliseconds(endDateTime)}${
      getMinutesFromMilliseconds(endDateTime) !== "00"
        ? `:${getMinutesFromMilliseconds(endDateTime)}`
        : ""
    }`,
    ampm: getAmOrPm(endDateTime),
  };

  return { startTime, endTime };
};
